import { put, select, takeLatest, all } from 'redux-saga/effects';
import { notify } from '@mrlannigan/reapop';
import { sagaError } from 'helpers/handleGenericSagaError';
import apiAuthCall from 'helpers/apiAuthCall';
import moment from 'moment';
import { omit } from 'lodash';
import { getCurrentUserEmail, getCurrentUserId } from 'services/users/selectors';
import { getAPITimeRange } from 'services/config/selectors';
import { generateAnnotationKey } from 'helpers/annotations';
import {
    actions,
    createNoteForm,
    editNoteForm,
    fetchAnnotationSuccess,
    fetchListSuccess,
    fetchList,
    fetchAnnotation as fetchAnnotationAction,
    fetchActiveNotes as fetchActiveNotesAction,
    fetchActiveNotesSuccess,
    deleteNoteSuccess
} from './duck';

import {
    notesActiveEndpoint,
    notesEndpoint,
    annotationsEndpoint,
    annotationsListEndpoint,
} from './selectors';

export function* fetchActiveNotes(api, action) {
    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(notesActiveEndpoint, action.payload)
            }
        );

        yield put(fetchActiveNotesSuccess(result.data));
    } catch (err) {
        yield put(notify({
            id: action.payload.transactionId,
            ...sagaError(err)
        }));
    }
}

export function* fetchAnnotation(api, action) {
    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(annotationsEndpoint, action.payload)
            }
        );

        yield put(fetchAnnotationSuccess(result.data));
    } catch (err) {
        yield put(notify({
            id: action.payload.transactionId,
            ...sagaError(err)
        }));
    }
}

export function* fetchAnnotationsList(api, action) {
    const notificationId = 'fetchAnnotationsList';
    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(annotationsListEndpoint, action.payload)
            }
        );

        yield put(fetchListSuccess(result.data));
    } catch (err) {
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* createNewNote(api, action) {
    const notificationId = 'createNote';
    try {
        const userEmail = yield select(getCurrentUserEmail);
        const userId = yield select(getCurrentUserId);

        const payload = Object.assign({
            user: userEmail
        }, omit(action.payload, 'handleToggle'));
        const { entityId, dashboard } = payload.meta;

        payload.key = generateAnnotationKey({
            annotationType: 'note',
            entityId,
            dashboard
        });
        payload.meta.userId = userId;
        payload.meta.start = moment(payload.meta.start).toISOString();
        payload.meta.end = moment(payload.meta.end).toISOString();

        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'post',
                url: yield select(notesEndpoint),
                data: payload
            }
        );

        yield put(createNoteForm.success());
        yield put(notify({
            id: notificationId,
            message: 'Note Created!',
            status: result.status
        }));
        const timeRange = yield select(getAPITimeRange);
        yield put(fetchActiveNotesAction({
            entityId,
            start: timeRange.start.format('YYYY-MM-DD'),
            end: timeRange.end.format('YYYY-MM-DD')
        }));
        action.payload.handleToggle();
    } catch (err) {
        yield put(createNoteForm.failure(err));
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* editNote(api, action) {
    const notificationId = 'editNote';
    try {
        const payload = action.payload.meta;
        const { entityId, adminContext } = action.payload;

        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'put',
                url: yield select(annotationsEndpoint, action.payload),
                data: payload
            }
        );

        yield put(editNoteForm.success());
        yield put(notify({
            id: notificationId,
            message: 'Note Updated!',
            status: result.status
        }));

        if (adminContext) {
            yield put(fetchAnnotationAction({ annotationId: action.payload.annotationId }));
        } else {
            const timeRange = yield select(getAPITimeRange);
            yield put(fetchActiveNotesAction({
                entityId,
                start: timeRange.start.format('YYYY-MM-DD'),
                end: timeRange.end.format('YYYY-MM-DD')
            }));
        }

        action.payload.handleToggle();
    } catch (err) {
        yield put(editNoteForm.failure(err));
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* deleteNote(api, confirmMsg, action) {
    const notificationId = 'deleteNote';

    if (!confirmMsg('Are you sure you wish to delete this note?')) { // eslint-disable-line no-alert
        return;
    }

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'delete',
                url: yield select(annotationsEndpoint, action.payload)
            }
        );

        yield put(notify({
            id: notificationId,
            message: `${action.payload.name || 'Note'} deleted!`,
            status: result.status
        }));

        yield put(deleteNoteSuccess());
        if (action.payload.entityId) {
            const timeRange = yield select(getAPITimeRange);
            yield put(fetchActiveNotesAction({
                entityId: action.payload.entityId,
                start: timeRange.start.format('YYYY-MM-DD'),
                end: timeRange.end.format('YYYY-MM-DD')
            }));
        }

        if (action.payload.refreshList === true) {
            yield put(fetchList());
        }
    } catch (err) {
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export default function* annotationSagas({ api }) {
    yield all([
        takeLatest(actions.FETCH_LIST, fetchAnnotationsList, api),
        takeLatest(actions.FETCH_ANNOTATION, fetchAnnotation, api),
        takeLatest(actions.FETCH_ACTIVE_NOTES, fetchActiveNotes, api),
        takeLatest(createNoteForm.REQUEST, createNewNote, api),
        takeLatest(editNoteForm.REQUEST, editNote, api),
        takeLatest(actions.DELETE_NOTE, deleteNote, api, window.confirm),
    ]);
}



// WEBPACK FOOTER //
// ./src/services/annotations/saga.js